/**
 * Hook para notificar al cajero cuando llegan nuevas órdenes a la cola.
 * Iter 51 — combina sonido (WebAudio) + Notification API (permiso opcional) + toast.
 *
 * Uso:
 *   useOrderNotifications(count, { enabled: true, soundEnabled: true, desktopEnabled: true })
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { ClipboardList } from "lucide-react";
import React from "react";

const STORAGE_KEY = "pos.order_notifications.v1";

export function loadOrderNotifPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { enabled: true, sound: true, desktop: true };
    return { enabled: true, sound: true, desktop: true, ...JSON.parse(raw) };
  } catch (_) { return { enabled: true, sound: true, desktop: true }; }
}

export function saveOrderNotifPrefs(prefs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch (_) {}
}

/** Genera un ping suave de 2 tonos (Web Audio API) — sin dependencias ni archivos. */
function playSoftPing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const notes = [
      { freq: 880, start: 0.0, dur: 0.14 },
      { freq: 1319, start: 0.14, dur: 0.18 }, // E6
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
    // Cerrar el context después
    setTimeout(() => { try { ctx.close(); } catch (_) {} }, 800);
  } catch (_) { /* ignora */ }
}

export async function ensureNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const p = await Notification.requestPermission();
    return p;
  } catch (_) { return "denied"; }
}

function showDesktopNotification(newCount, addedCount) {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  try {
    const body = addedCount > 1
      ? `${addedCount} órdenes nuevas · ${newCount} pendientes`
      : `Hay ${newCount} orden${newCount !== 1 ? "es" : ""} pendiente${newCount !== 1 ? "s" : ""}`;
    const n = new Notification("Nueva orden en cola", {
      body,
      icon: "/favicon.ico",
      tag: "sales-order-queue",  // reemplaza previas
      requireInteraction: false,
      silent: true,  // ya reproducimos nuestro propio sonido
    });
    n.onclick = () => {
      window.focus();
      window.location.href = "/sales-orders";
      n.close();
    };
    setTimeout(() => { try { n.close(); } catch (_) {} }, 8000);
  } catch (_) { /* algunos navegadores lo bloquean en ciertos contextos */ }
}

export default function useOrderNotifications(count, prefs = {}) {
  const { enabled = true, sound = true, desktop = true } = prefs;
  const prevRef = useRef(null);
  // Ignoramos los cambios durante los primeros 3s tras el mount para
  // saltar el "primer fetch inicial" que sube de 0 al valor real.
  const graceUntilRef = useRef(Date.now() + 3000);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = count;

    if (Date.now() < graceUntilRef.current) return;
    if (!enabled) return;
    if (typeof count !== "number" || typeof prev !== "number") return;

    // Solo notificar en INCREMENTO
    if (count > prev) {
      const delta = count - prev;

      // Evitar sonido/desktop si el usuario ya está viendo la cola
      const isOnQueue = window.location.pathname.startsWith("/sales-orders");

      toast.info(
        delta > 1 ? `${delta} órdenes nuevas en cola` : "Nueva orden en cola",
        {
          description: `${count} pendiente${count !== 1 ? "s" : ""}`,
          duration: 5000,
          action: isOnQueue ? undefined : {
            label: "Ver",
            onClick: () => { window.location.href = "/sales-orders"; },
          },
        },
      );

      if (!isOnQueue) {
        if (sound) playSoftPing();
        if (desktop) showDesktopNotification(count, delta);
      }
    }
  }, [count, enabled, sound, desktop]);
}
